import { useEffect, useState } from "react"

import { useLazyGetAlertStatusesQuery } from "store/api/widgetsApi"
import { ApiTypes } from "types"

import WidgetsLayout from "layouts/WidgetsLayout"
import { BarChart } from "components/common/BarChart"
import { dummyAlertsStatusesWidgetData } from "pages/contractor/AlertsPage/dummyAlertsData"

import styles from "./index.module.scss"

interface AlertStatusesWidgetProps {
  hasAlertsAccess?: boolean
  unsupported?: boolean
  updateIndex: number
  dateRange?: [string, string]
}

const initialWidgetStatusesData: ApiTypes.Res.AlertStatusesWidget = {
  active: 0,
  frozen: 0,
  acknowledged: 0,
  alerts_count: 0,
}

export const WidgetAlertStatuses = ({
  dateRange,
  hasAlertsAccess = true,
  unsupported: unsupportedProp,
  updateIndex,
}: AlertStatusesWidgetProps) => {
  const unsupported = unsupportedProp ?? !hasAlertsAccess

  const [widgetData, setWidgetData] = useState(initialWidgetStatusesData)
  const [triggerGetAlertStatuses, { isFetching }] = useLazyGetAlertStatusesQuery()

  useEffect(() => {
    if (unsupported) {
      setWidgetData(dummyAlertsStatusesWidgetData)
      return
    }

    const fetchData = async () => {
      try {
        const res = await triggerGetAlertStatuses({ date_start: dateRange?.[0], date_end: dateRange?.[1] })
        if (res?.data?.data) {
          setWidgetData({
            active: res.data.data.active ?? 0,
            frozen: res.data.data.frozen ?? 0,
            acknowledged: res.data.data.acknowledged ?? 0,
            alerts_count: res.data.data.alerts_count ?? 0,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [unsupported, updateIndex, triggerGetAlertStatuses, dateRange])

  const data = unsupported ? dummyAlertsStatusesWidgetData : widgetData
  return <AlertStatusesWidget data={data} isFetching={!unsupported && isFetching} />
}

export const AlertStatusesWidget = ({
  data,
  isFetching = false,
}: {
  data?: Partial<ApiTypes.Res.AlertStatusesWidget>
  isFetching?: boolean
}) => {
  const statuses = {
    active: data?.active ?? 0,
    frozen: data?.frozen ?? 0,
    acknowledged: data?.acknowledged ?? 0,
  }

  const CHART_COLORS = {
    active: "#22C55E",
    frozen: "#475569",
    acknowledged: "#2DB2C8",
  } as const

  const hasNoData = statuses.active + statuses.frozen + statuses.acknowledged === 0

  return (
    <WidgetsLayout title="Alerts Statuses" isFetching={isFetching}>
      <div className={styles.chartWrapper}>
        <BarChart
          showEmptyState
          items={
            hasNoData
              ? []
              : [
                  { color: CHART_COLORS.active, value: statuses.active },
                  { color: CHART_COLORS.frozen, value: statuses.frozen },
                  { color: CHART_COLORS.acknowledged, value: statuses.acknowledged },
                ]
          }
        />
        {!hasNoData && (
          <footer className={styles.footer}>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.active }}>
                Active
              </span>
              <span className={styles.footerValue}>{statuses.active}</span>
            </div>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.frozen }}>
                Frozen
              </span>
              <span className={styles.footerValue}>{statuses.frozen}</span>
            </div>
            <div className={styles.footerItem}>
              <span className={styles.footerNote} style={{ ["--dot-color" as string]: CHART_COLORS.acknowledged }}>
                Acknowledged
              </span>
              <span className={styles.footerValue}>{statuses.acknowledged}</span>
            </div>
          </footer>
        )}
      </div>
    </WidgetsLayout>
  )
}
